import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Plus, Search } from "lucide-react";
import {
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VButton,
  VDataTable,
  VInput,
  VSelect,
} from "@/components/ui";
import type { RequestStatus, ReservationRequest } from "@/v2/shared/model";
import { formatDate } from "@/v2/shared/format";
import { requestStatusMeta } from "@/v2/shared/statusMeta";
import { RequestStatusBadge } from "@/v2/shared/statusBadges";
import { useCurrentUser } from "@/v2/auth/useCurrentUser";
import { useRequests, useRequestsByRequester } from "@/v2/data/queries";

const STATUS_OPTIONS: ReadonlyArray<RequestStatus> = ["OPEN", "ACCEPTED", "CLOSED"];

export function RequestListPage(): React.JSX.Element {
  const user = useCurrentUser();
  const isManager = user.role === "manager";
  const navigate = useNavigate();

  const all = useRequests();
  const mine = useRequestsByRequester(user.id);
  const { data, isPending, isError } = isManager ? all : mine;

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<RequestStatus | "all">("all");

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data ?? []).filter((r) => {
      const matchesStatus = status === "all" || r.status === status;
      const matchesSearch =
        term === "" ||
        r.title.toLowerCase().includes(term) ||
        r.number.toLowerCase().includes(term) ||
        r.address.toLowerCase().includes(term);
      return matchesStatus && matchesSearch;
    });
  }, [data, search, status]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Aanvragen"
        subtitle={
          isManager
            ? "Alle aanvragen voor mobiel cameratoezicht."
            : "Jouw ingediende aanvragen."
        }
        actions={
          <VButton asChild>
            <Link to="/aanvragen/nieuw">
              <Plus className="size-4" aria-hidden />
              Nieuwe aanvraag
            </Link>
          </VButton>
        }
      />

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-56">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <VInput
            className="pl-9"
            placeholder="Zoek op titel, nummer of adres…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <VSelect
          aria-label="Filter op status"
          className="w-52"
          value={status}
          onChange={(e) => {
            const value = e.target.value;
            setStatus(value === "all" ? "all" : (STATUS_OPTIONS.find((s) => s === value) ?? "all"));
          }}
        >
          <option value="all">Alle statussen</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {requestStatusMeta[s].label}
            </option>
          ))}
        </VSelect>
      </div>

      {isPending ? (
        <LoadingState label="Aanvragen laden…" />
      ) : isError ? (
        <ErrorMessage title="Kon de aanvragen niet laden" />
      ) : (
        <VDataTable<ReservationRequest>
          rows={rows}
          getRowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/aanvragen/${r.id}`)}
          emptyState={
            <EmptyState
              title="Geen aanvragen gevonden"
              description="Pas je zoekterm of filter aan, of start een nieuwe aanvraag."
            />
          }
          columns={[
            {
              key: "number",
              header: "Nummer",
              cell: (r) => r.number,
              className: "whitespace-nowrap tabular-nums text-muted-foreground",
            },
            { key: "title", header: "Titel", cell: (r) => r.title, className: "font-medium" },
            { key: "address", header: "Locatie", cell: (r) => r.address },
            // Aanvullend voor de beheerder: aanvrager en dienst (CB-8).
            ...(isManager
              ? [
                  {
                    key: "requester",
                    header: "Aanvrager",
                    cell: (r: ReservationRequest) =>
                      `${r.requesterFirstName} ${r.requesterLastName}`,
                  },
                  {
                    key: "service",
                    header: "Dienst",
                    cell: (r: ReservationRequest) => r.requesterService,
                  },
                ]
              : []),
            {
              key: "start",
              header: "Begin",
              cell: (r) => formatDate(r.startDate),
              className: "whitespace-nowrap tabular-nums",
            },
            {
              key: "end",
              header: "Einde",
              cell: (r) => formatDate(r.endDate),
              className: "whitespace-nowrap tabular-nums",
            },
            {
              key: "extension",
              header: "Verlenging",
              cell: (r) => (r.extension === "possible" ? "Mogelijk" : "Vast"),
            },
            {
              key: "status",
              header: "Status",
              cell: (r) => <RequestStatusBadge status={r.status} />,
            },
          ]}
        />
      )}
    </div>
  );
}
